import { Container, Box, Typography, Grid, Paper, Link, Breadcrumbs, Button, TextField, MenuItem, FormControl, InputLabel, Select, Divider, Alert, Snackbar, CircularProgress, InputAdornment } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Hero from '../components/common/Hero';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { submitContactForm, getBoardMembers } from '../services/api';
import PeopleIcon from '@mui/icons-material/People';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PersonIcon from '@mui/icons-material/Person';
import SubjectIcon from '@mui/icons-material/Subject';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MessageIcon from '@mui/icons-material/Message';

const ContactsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    recipient: '',
    subject: '',
    message: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [boardOptions, setBoardOptions] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);

  // Fetch board members from Strapi on component mount
  useEffect(() => {
    const fetchBoardMembers = async () => {
      try {
        setLoadingRecipients(true);
        const recipients = await getBoardMembers();

        console.log('Board members response:', recipients);

        if (recipients && Array.isArray(recipients) && recipients.length > 0) {
          // Map recipients to format needed for dropdown
          const formattedOptions = recipients.map(recipient => ({
            value: recipient.email,
            label: recipient.name + ' - ' + recipient.position
          }));

          // Add "All Board Members" option
          formattedOptions.push({ value: 'board@brynleighestates.com', label: 'All Board Members' });

          console.log('Formatted options:', formattedOptions);
          setBoardOptions(formattedOptions);
        } else {
          console.log('No recipients found, using fallback options');
          // Fallback to static options if no data from API
          setBoardOptions(fallbackBoardOptions);
        }
      } catch (error) {
        console.error('Failed to load recipients:', error);
        setBoardOptions(fallbackBoardOptions);
      } finally {
        setLoadingRecipients(false);
      }
    };

    fetchBoardMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to extract error message from error object
  const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;

    if (error && typeof error === 'object') {
      // Handle Strapi error format
      if (typeof error.message === 'string') return error.message;

      // Handle nested error objects from Strapi
      if (error.data && error.data.error) {
        const strapiError = error.data.error;
        if (typeof strapiError === 'string') return strapiError;
        if (strapiError && typeof strapiError.message === 'string') return strapiError.message;
        return 'Error from server. Please try again.';
      }
    }

    return 'An unexpected error occurred. Please try again later.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    loadingRef.current = true;

    // Set a timeout to prevent indefinite spinner
    const timeoutId = setTimeout(() => {
      if (loadingRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setSnackbar({
          open: true,
          message: 'Request took too long, but your message may have been sent. Please check with the recipient.',
          severity: 'warning'
        });
      }
    }, 12000); // 12 seconds timeout

    try {
      const result = await submitContactForm(formData);

      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      // Explicitly check for success true
      if (result && result.success === true) {
        // Clear form first
        setFormData({
          name: '',
          email: '',
          recipient: '',
          subject: '',
          message: ''
        });

        // Then show success message
        setSnackbar({
          open: true,
          message: 'Message sent successfully! The HOA board member will get back to you soon.',
          severity: 'success'
        });
      } else {
        // Show error message - make sure we're getting a string
        let errorMessage = 'Failed to send message. Please try again later.';

        try {
          if (result && result.error) {
            errorMessage = getErrorMessage(result.error);
          }
        } catch (e) {
          console.error('Error formatting error message:', e);
        }

        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);

      console.error('[Form] Error submitting form:', error);

      // Make sure we're getting a string error message
      let errorMessage = 'An unexpected error occurred. Please try again later.';

      try {
        errorMessage = getErrorMessage(error);
      } catch (e) {
        console.error('Error formatting error message:', e);
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      // Clear the timeout to prevent it from firing if it hasn't yet
      clearTimeout(timeoutId);
      loadingRef.current = false;
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  // Fallback board members in case API fails
  const fallbackBoardOptions = [
    { value: 'president@brynleighestates.com', label: 'Kassandra Larson - President' },
    { value: 'vp@brynleighestates.com', label: 'Kelly Moore - Vice President' },
    { value: 'treasurer@brynleighestates.com', label: 'Daniel E. Hermony Jr. - Treasurer' },
    { value: 'tech@brynleighestates.com', label: 'Josh Holtzclaw - Technology' },
    { value: 'arc@brynleighestates.com', label: 'Matt Smith - ARC Chairman' },
    { value: 'arc-committee@brynleighestates.com', label: 'Coty - ARC Committee' },
    { value: 'board@brynleighestates.com', label: 'All Board Members' }
  ];

  // Contact information organized by categories
  const contactCategories = [
    {
      title: "Emergency Services",
      contacts: [
        { name: "Emergency Response", phone: "911", website: null },
        { name: "Digging (before you dig)", phone: "811", website: "http://call811.com/before-you-dig" },
        { name: "Chelsea Fire - non emergency", phone: "(205) 678-6060", website: null },
        { name: "Shelby County Sheriff - non emergency", phone: "(205) 669-4181", website: "http://shelbylepb.com/index.php?submenu=home&src=" },
        { name: "Alabama Poison Control", phone: "(800) 462-0800", website: null },
        { name: "Birmingham Poison Control", phone: "(800) 292-6678", website: null },
        { name: "Chelsea Compact2020", phone: "(205) 670-6101", website: "www.shelbyal.com/748/Compact-2020" },
        { name: "Domestic Violence Shelter of Shelby County (No Caller ID)", phone: "(205) 664-4357", website: "https://safehouse.org/" }
      ]
    },
    {
      title: "Local Government & Services",
      contacts: [
        { name: "New Comer Information Shelby County", phone: null, website: "www.shelbyal.com" },
        { name: "Chelsea City Hall", phone: "(205) 678-8455", website: "www.cityofchelsea.com" },
        { name: "Chelsea Public Library", phone: "(205) 618-9735", website: "www.chelsealibraryonline.com" },
        { name: "Chelsea Post Office", phone: "(205) 678-7915", website: null },
        { name: "Board of Registrars", phone: "(205) 669-3913", website: "www.shelbyal.com/205/Board-of-Registrars" },
        { name: "Chelsea Community Center", phone: "(205) 677-2052", website: "www.cityofchelsea.com/chelsea-community-center.html" }
      ]
    },
    {
      title: "Schools",
      contacts: [
        { name: "Forest Oaks Elementary School (FOES bus# 18-02)", phone: "(205) 682-7220", website: "www.shelbyed.k12.al.us/schools/foes" },
        { name: "Chelsea Middle School (ChMS & ChHS bus# 18-22)", phone: "(205) 682-7210", website: "www.shelbyed.k12.al.us/schools/chms" },
        { name: "Chelsea High School", phone: "(205) 682-7200", website: "www.shelbyed.k12.al.us/schools/chhs" },
        { name: "Shelby County Board of Education", phone: "(205) 682-7000", website: "www.shelbyed.k12.al.us" },
        { name: "Shelby County Board of Education - Transportation Dept.", phone: "(205) 682-7130", website: "www.shelbyed.k12.al.us/transportation.htm" }
      ]
    },
    {
      title: "Utilities",
      contacts: [
        { name: "Spire Energy (previously Alagasco)", phone: "(800) 292-4008", website: "www.spireenergy.com" },
        { name: "Alabama Power (Service)", phone: "(800) 245-2244", website: "www.alabamapower.com" },
        { name: "AT&T - Residential, Wireless, Internet, U-verse", phone: "(888) 757-6500", website: "www.att.com" },
        { name: "Pelham Water (City of Pelham)", phone: "(205) 620-6420", website: "https://pelhamalabama.gov/202/Water-Sewer" },
        { name: "Spectrum/Charter Cable", phone: "(800) 892-4357", website: "www.spectrum.com" },
        { name: "Waste Pro", phone: "(205) 605-0800", website: "www.wasteprousa.com" },
        { name: "Propane, Butane Gas, WanBon Gas", phone: "(205) 678-6681", website: "www.wanbongas.com" },
        { name: "Sewer, NOVUS Double Oak LLC", phone: "(205) 987-8352", website: "www.swwc.com/north-shelby" }
      ]
    },
    {
      title: "Media",
      contacts: [
        { name: "Shelby County Reporter (Paper)", phone: "(205) 669-3131", website: "www.shelbycountyreporter.com" },
        { name: "Birmingham News (Paper)", phone: null, website: "www.al.com/birmingham" }
      ]
    },
    {
      title: "Medical",
      contacts: [
        { name: "Emergency Vet", phone: "(205) 413-8989", website: "www.steelcityemergencyvets.com" },
        { name: "Children's Hospital", phone: "(205) 638-9100", website: "www.childrensal.org" },
        { name: "Grandview Hospital", phone: "(205) 971-1000", website: "www.grandviewhealth.com" },
        { name: "Brookwood Free-Standing ER 119", phone: "(205) 725-6800", website: "www.brookwoodbaptisthealth.com/our-locations/freestanding-emergency-department" },
        { name: "St. Vincent's Hospital", phone: "(205) 939-7000", website: "https://healthcare.ascension.org/Locations/Alabama/ALBIR/Birmingham-St-Vincents-Birmingham" },
        { name: "St. Vincent's 119", phone: "(205) 408-6600", website: "https://healthcare.ascension.org/Locations/Alabama/ALBIR/Hoover-St-Vincents-One-Nineteen" }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Contact Us | Brynleigh Estates HOA</title>
        <meta name="description" content="Contact the Brynleigh Estates HOA board members and find important contact information for Chelsea neighborhoods, emergency services, and local utilities." />
        <link rel="canonical" href="https://brynleighestates.com/contacts" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/contacts" />
        <meta property="og:title" content="Contact Us | Brynleigh Estates HOA" />
        <meta property="og:description" content="Contact the Brynleigh Estates HOA board members and find important contact information for Chelsea neighborhoods and local services." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Contact the Brynleigh Estates HOA board members and find important local contact information." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>

      <Hero
        imageUrl="/images/hills-bg.jpeg"
        title="Contact Us"
        subtitle="Get in touch with the HOA board and find important local contacts"
        height="small"
      />

      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link
              underline="hover"
              color="inherit"
              href="/"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Contact Us
            </Typography>
          </Breadcrumbs>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{ mb: 4 }}
          >
            Back to Home
          </Button>

          {/* HOA Contact Form Section */}
          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Contact the HOA Board
            </Typography>
            <Typography variant="body1" paragraph>
              Use this form to send a message to the HOA board members or committees. We'll respond to your inquiry as soon as possible.
            </Typography>

            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Your Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth required disabled={loading || loadingRecipients}>
                          <InputLabel id="recipient-label">Send To</InputLabel>
                          <Select
                            labelId="recipient-label"
                            name="recipient"
                            value={formData.recipient}
                            onChange={handleChange}
                            label="Send To"
                            startAdornment={
                              <InputAdornment position="start">
                                <MarkEmailReadIcon />
                              </InputAdornment>
                            }
                          >
                            {loadingRecipients ? (
                              <MenuItem value="" disabled>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <CircularProgress size={20} sx={{ mr: 1 }} />
                                  Loading recipients...
                                </Box>
                              </MenuItem>
                            ) : (
                              boardOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          variant="outlined"
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SubjectIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          multiline
                          rows={5}
                          variant="outlined"
                          disabled={loading}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MessageIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                          size="large"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ p: 3, height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="h5" gutterBottom>
                    Board Contact Information
                  </Typography>
                  <Typography variant="body2" paragraph>
                    You can also reach out to board members directly:
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      General Inquiries
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:board@brynleighestates.com">
                        board@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Board Members
                  </Typography>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Kassandra Larson - President
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:president@brynleighestates.com" sx={{ fontSize: '0.875rem' }}>
                        president@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Kelly Moore - Vice President
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:vp@brynleighestates.com" sx={{ fontSize: '0.875rem' }}>
                        vp@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Daniel E. Hermony Jr. - Treasurer
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:treasurer@brynleighestates.com" sx={{ fontSize: '0.875rem' }}>
                        treasurer@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Matt Smith - ARC Chairman
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:arc@brynleighestates.com" sx={{ fontSize: '0.875rem' }}>
                        arc@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      Josh Holtzclaw - Technology
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                      <EmailIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="mailto:tech@brynleighestates.com" sx={{ fontSize: '0.875rem' }}>
                        tech@brynleighestates.com
                      </Link>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                      Quick Links
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="/board-members" sx={{ fontSize: '0.875rem' }}>
                        Board and Committee Members
                      </Link>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <HomeWorkIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                      <Link href="/arc-request" sx={{ fontSize: '0.875rem' }}>
                        Submit ARC Request
                      </Link>
                    </Box>
                  </Box>

                  <Typography variant="body2" paragraph>
                    For home modification approvals, please visit our <Link href="/arc-request">Architectural Review Request</Link> page.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 5 }} />

          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 4 }}>
            Important Local Contacts
          </Typography>

          {contactCategories.map((category, index) => (
            <Box key={index} sx={{ mb: 5 }}>
              <Typography variant="h5" component="h3" gutterBottom sx={{
                borderBottom: '2px solid',
                borderColor: 'primary.main',
                pb: 1,
                mb: 3
              }}>
                {category.title}
              </Typography>

              <Grid container spacing={3}>
                {category.contacts.map((contact, contactIndex) => (
                  <Grid item xs={12} sm={6} md={4} key={contactIndex}>
                    <Paper elevation={2} sx={{
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}>
                      <Typography variant="h6" component="h4" gutterBottom>
                        {contact.name}
                      </Typography>

                      {contact.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography>
                            <Link href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`} underline="hover">
                              {contact.phone}
                            </Link>
                          </Typography>
                        </Box>
                      )}

                      {contact.website && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto' }}>
                          <LinkIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography noWrap>
                            <Link
                              href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              Website
                            </Link>
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key={new Date().getTime()}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {typeof snackbar.message === 'string' ? snackbar.message : 'An error occurred'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ContactsPage; 